import { Platform } from 'react-native';
import type { ReviewTemplate } from '@exhale/contracts';
import { FULL_REVIEW_TEMPLATES_PATH } from './templateApiPaths';

/**
 * Platform-specific service wrapper
 * Returns mock services for web platform to avoid SQLite import errors
 */

interface MockOfflineDatabase {
  initialize: () => Promise<void>;
  close: () => Promise<void>;
  [key: string]: any;
}

interface MockOfflineReviewService {
  initialize: () => Promise<void>;
  isServiceInitialized: () => boolean;
  cleanup: () => void;
  [key: string]: any;
}

interface MockCacheService {
  initialize: () => Promise<void>;
  syncAllCaches: () => Promise<void>;
  getCachedTemplates: () => Promise<any[]>;
  refreshTemplates: () => Promise<any[]>;
  isTemplatesStale: (maxAgeMinutes: number) => Promise<boolean>;
  [key: string]: any;
}

interface MockPhotoQueueService {
  initialize: () => Promise<void>;
  [key: string]: any;
}

// Mock implementations for web
const mockOfflineDatabase: MockOfflineDatabase = {
  initialize: async () => {},
  close: async () => {},
  createReview: async () => 'mock-id',
  getReview: async () => null,
  updateReview: async () => {},
  deleteReview: async () => {},
  getAllReviews: async () => [],
  createResponse: async () => 'mock-id',
  getResponsesForReview: async () => [],
  getResponsesByReviewId: async () => [], // Defense-in-depth for background prefetch
  updateResponse: async () => {},
  deleteResponse: async () => {},
  cacheTemplate: async () => {},
  getCachedTemplates: async () => [],
  cacheCategories: async () => {},
  getCachedCategories: async () => [],
  cacheItems: async () => {},
  getCachedItems: async () => [],
  cacheRooms: async () => {},
  getCachedRooms: async () => [],
  addPhotoToQueue: async () => 1,
  getPhotoQueueItems: async () => [],
  updatePhotoQueueItem: async () => {},
  deletePhotoQueueItem: async () => {},
  createOfflineRoom: async () => 'mock-id',
  getOfflineRooms: async () => [],
  deleteOfflineRoom: async () => {},
  createOfflineOnDemandItem: async () => 'mock-id',
  getOfflineOnDemandItems: async () => [],
  deleteOfflineOnDemandItem: async () => {},
};

const mockOfflineReviewService: MockOfflineReviewService = {
  initialize: async () => {},
  isServiceInitialized: () => true,
  cleanup: () => {},
  createOfflineReview: async () => ({ id: 'mock-id' }),
  updateOfflineReview: async () => {},
  deleteOfflineReview: async () => {},
  getOfflineReview: async () => ({ id: 'mock-id' }),
  getOfflineReviews: async () => [],
  createOfflineResponse: async () => ({ id: 'mock-id' }),
  updateOfflineResponse: async () => {},
  deleteOfflineResponse: async () => {},
  createOfflineRoom: async () => 'mock-id',
  createOfflineOnDemandItem: async () => 'mock-id',
  syncOfflineReview: async () => ({ success: true }),
  syncAllOfflineData: async () => ({ success: true, synced: 0, failed: 0 }),
  getRecoverableWorkItems: async () => [],
  recoverSelectedWorkItemIds: async () => ({ recovered: 0, skipped: 0, failed: 0 }),
  keepServerForSelectedWorkItemIds: async () => ({ keptServer: 0, skipped: 0, failed: 0 }),
};

const mockCacheService: MockCacheService = {
  initialize: async () => {},
  syncAllCaches: async () => {},
  getCachedTemplates: async () => [],
  refreshTemplates: async () => [],
  isTemplatesStale: async () => true,
  getCachedCategories: async () => [],
  getCachedItems: async () => [],
  getCachedRooms: async () => [],
  cacheTemplates: async () => {},
  cacheCategories: async () => {},
  cacheItems: async () => {},
  cacheRooms: async () => {},
  refreshRooms: async () => [], // Fix for web - returns empty array
  getTemplates: async (forceRefresh?: boolean) => {
    // Fix for web - bypass cache and fetch directly from API
    try {
      const { api } = require('./api') as typeof import('./api');
      const templates = await api.get<ReviewTemplate[]>(FULL_REVIEW_TEMPLATES_PATH);
      return templates;
    } catch (error) {
      console.error('[MockCacheService] Failed to fetch templates:', error);
      return [];
    }
  },
  getConfiguration: () => ({ initialized: true }), // Fix for web - return mock config
  clearAllCaches: async () => {},
};

const mockPhotoQueueService: MockPhotoQueueService = {
  initialize: async () => {},
  addToQueue: async () => 1,
  processQueue: async () => {},
  getQueueStatus: () => ({ pending: 0, uploading: 0, failed: 0 }),
  retryFailed: async () => {},
  forceRetryFailedUploads: async () => ({
    successful: [],
    failed: [],
    totalProcessed: 0,
    reset: 0,
    missing: 0,
    stillFailed: 0,
  }),
  getLocalFileStats: async () => ({ available: 0, missing: 0 }),
  clearCompleted: async () => {},
};

// Export platform-specific services
export const getOfflineDatabase = () => {
  if (Platform.OS === 'web') {
    return mockOfflineDatabase;
  }
  // Only import the real service for mobile platforms
  const { offlineDatabase } = require('./offlineDatabase');
  return offlineDatabase;
};

export const getOfflineReviewService = () => {
  if (Platform.OS === 'web') {
    return mockOfflineReviewService;
  }
  // Only import the real service for mobile platforms
  const { offlineReviewService } = require('./offlineReviewService');
  return offlineReviewService;
};

export const getCacheService = () => {
  if (Platform.OS === 'web') {
    return mockCacheService;
  }
  // Only import the real service for mobile platforms
  const { cacheService } = require('./cacheService');
  return cacheService;
};

export const getPhotoQueueService = () => {
  if (Platform.OS === 'web') {
    return mockPhotoQueueService;
  }
  // Only import the real service for mobile platforms
  const { photoQueueService } = require('./photoQueueService');
  return photoQueueService;
};
